import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Coins,
  Gift,
  Share2,
  CheckCircle2,
  ArrowRight,
  MessageCircle,
  Sparkles,
  Trophy,
  Zap,
  ShieldCheck,
} from "lucide-react";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";
import FloatingWA from "@/components/sections/FloatingWA";

const steps = [
  {
    icon: Share2,
    title: "1. Rekomendasikan",
    desc: "Bagikan info jasa saya ke teman UMKM Anda — via WA, IG story, grup, atau mulut ke mulut. Sertakan nama Anda sebagai perekomendasi.",
  },
  {
    icon: MessageCircle,
    title: "2. Teman Anda Chat",
    desc: "Ketika mereka konsultasi via WA, cukup sebut nama Anda. Saya akan catat sebagai referral untuk Anda.",
  },
  {
    icon: CheckCircle2,
    title: "3. Deal Sukses",
    desc: "Setelah project mereka closed dan pembayaran DP diterima, referral Anda otomatis divalidasi.",
  },
  {
    icon: Coins,
    title: "4. Komisi Cair",
    desc: "Komisi 15% dari nilai project ditransfer ke rekening Anda paling lambat 3 hari kerja setelah proyek jalan.",
  },
];

const benefits = [
  {
    icon: Coins,
    title: "Komisi 15% Tanpa Batas",
    desc: "Referral 1 klien = 1 komisi. Referral 10 klien = 10 komisi. Tidak ada limit bulanan.",
  },
  {
    icon: Zap,
    title: "Cair Cepat 3 Hari",
    desc: "Ditransfer maksimal 3 hari kerja setelah DP klien masuk. Tidak nunggu proyek selesai.",
  },
  {
    icon: ShieldCheck,
    title: "Transparan & Terlacak",
    desc: "Setiap referral tercatat rapi. Anda bisa minta laporan kapan saja lewat WhatsApp.",
  },
  {
    icon: Gift,
    title: "Bonus Top Referrer",
    desc: "3 top referrer per kuartal dapat bonus tambahan Rp 500rb + free website upgrade untuk bisnis Anda.",
  },
];

const examples = [
  { pkg: "UMKM Starter", price: 1500000, commission: 225000 },
  { pkg: "UMKM Bisnis", price: 2900000, commission: 435000 },
  { pkg: "UMKM Toko Online", price: 4900000, commission: 735000 },
  { pkg: "Custom Premium", price: 7900000, commission: 1185000 },
];

const faqs = [
  {
    q: "Saya bukan klien Anda, boleh ikut referral?",
    a: "Boleh banget! Program ini terbuka untuk semua orang — baik klien lama, teman, kerabat, atau siapapun yang mau bantu rekomendasikan. Tidak perlu daftar dulu, cukup chat saya via WhatsApp saat mau mulai.",
  },
  {
    q: "Bagaimana kalau klien saya batal tengah jalan?",
    a: "Komisi hanya dibayarkan setelah DP masuk. Kalau DP sudah dibayar dan proyek jalan, komisi Anda tetap saya kirim penuh — meski di tengah jalan ada kendala dari sisi klien.",
  },
  {
    q: "Ada minimum jumlah klien untuk cair?",
    a: "Tidak ada. Bahkan 1 klien pertama Anda langsung dapat komisi cair, tidak perlu tunggu akumulasi.",
  },
  {
    q: "Bisa refer klien di luar Jabodetabek?",
    a: "Bisa. Saya melayani seluruh Indonesia. Meeting bisa online via Google Meet / WhatsApp. Semua komunikasi & handover file digital.",
  },
  {
    q: "Berlaku sampai kapan?",
    a: "Program ini permanen (tidak terbatas waktu). Selama saya masih terima order, komisi Anda tetap berlaku. Kalau ada perubahan, akan saya kabari lewat WhatsApp Anda dulu.",
  },
];

export default function Referral() {
  useEffect(() => {
    document.title =
      "Program Referral 15% Komisi | Bantu Rekomendasi, Dapat Cuan — Agung Darmawan";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Ikut program referral jasa website UMKM. Rekomendasikan teman → deal → dapat komisi 15% cair 3 hari. Tanpa limit, tanpa daftar ribet."
    );
    window.scrollTo(0, 0);
  }, []);

  const waJoin = encodeURIComponent(
    "Halo Kak Agung, saya mau ikut Program Referral 15%. Nama saya: [isi nama], saya mau mulai rekomendasikan jasa website ke teman-teman UMKM saya."
  );

  return (
    <div className="min-h-screen bg-[var(--brand-cream)]">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden bg-[var(--brand-ink)] text-white">
        <div className="absolute inset-0 dotted-grid opacity-10 pointer-events-none" />
        <div className="pointer-events-none absolute top-20 -right-24 w-96 h-96 rounded-full bg-[var(--brand-gold)] blur-3xl opacity-25" />
        <div className="pointer-events-none absolute -bottom-20 -left-24 w-96 h-96 rounded-full bg-[var(--brand-azure)] blur-3xl opacity-20" />

        <div className="relative mx-auto max-w-5xl px-5 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-gold)] text-[var(--brand-ink)] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em]">
            <Trophy className="w-3.5 h-3.5" />
            Program Referral
          </div>
          <h1 className="mt-5 font-display text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.02]">
            Rekomendasikan Teman,<br />
            <em className="not-italic text-[var(--brand-gold)]">Dapat 15% Komisi</em>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg text-white/75 leading-relaxed">
            Bantu teman UMKM Anda punya website profesional, sekaligus dapat komisi
            tunai <strong className="text-white">15% dari nilai project</strong>. Tidak ada limit,
            tidak ada syarat ribet, cair 3 hari kerja.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a
              href={`https://wa.me/628211001473?text=${waJoin}`}
              target="_blank"
              rel="noreferrer"
              data-testid="referral-join-cta"
              className="cta-shine inline-flex items-center gap-2 rounded-full bg-[var(--brand-gold)] text-[var(--brand-ink)] px-6 py-3.5 text-sm font-bold hover:bg-[#ffcc3b] transition"
            >
              <Sparkles className="w-4 h-4" />
              Gabung Program Sekarang
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#cara-kerja"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 backdrop-blur px-6 py-3.5 text-sm font-semibold hover:bg-white/10 transition"
            >
              Lihat Cara Kerja
            </a>
          </div>

          {/* Big number */}
          <div className="mt-14 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-4">
              <div className="text-3xl md:text-4xl font-bold text-[var(--brand-gold)]">15%</div>
              <div className="mt-1 text-xs text-white/60">Komisi Per Deal</div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-4">
              <div className="text-3xl md:text-4xl font-bold text-[var(--brand-gold)]">3 hari</div>
              <div className="mt-1 text-xs text-white/60">Komisi Cair</div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-4">
              <div className="text-3xl md:text-4xl font-bold text-[var(--brand-gold)]">∞</div>
              <div className="mt-1 text-xs text-white/60">Tanpa Limit</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-azure-soft)] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-azure)]">
              <Users className="w-3.5 h-3.5" />
              Keuntungan Anda
            </div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold text-[var(--brand-ink)]">
              Kenapa Program Ini Menarik?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl bg-white border border-[var(--brand-border)] hover:shadow-lg transition-all"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--brand-ink)] text-[var(--brand-gold)]">
                  <b.icon className="w-5 h-5" />
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold text-[var(--brand-ink)]">
                  {b.title}
                </h3>
                <p className="mt-2 text-sm text-[var(--brand-gray-500)] leading-relaxed">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Table */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-5">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-gold)]/20 text-[var(--brand-ink)] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em]">
              <Coins className="w-3.5 h-3.5" />
              Simulasi Komisi
            </div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold text-[var(--brand-ink)]">
              Berapa yang Bisa Anda Dapat?
            </h2>
            <p className="mt-3 text-[var(--brand-gray-500)]">
              15% dari nilai paket. Semakin premium paket klien, semakin besar komisi Anda.
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-[var(--brand-border)]">
            <table className="w-full">
              <thead className="bg-[var(--brand-ink)] text-white">
                <tr>
                  <th className="text-left py-4 px-6 text-xs uppercase tracking-wider font-bold">
                    Paket
                  </th>
                  <th className="text-right py-4 px-6 text-xs uppercase tracking-wider font-bold">
                    Harga Klien
                  </th>
                  <th className="text-right py-4 px-6 text-xs uppercase tracking-wider font-bold bg-[var(--brand-gold)] text-[var(--brand-ink)]">
                    Komisi Anda
                  </th>
                </tr>
              </thead>
              <tbody>
                {examples.map((e, i) => (
                  <tr key={i} className="border-b border-[var(--brand-border)] last:border-b-0 hover:bg-[var(--brand-cream)] transition">
                    <td className="py-4 px-6 font-semibold text-[var(--brand-ink)]">{e.pkg}</td>
                    <td className="py-4 px-6 text-right text-[var(--brand-gray-500)]">
                      Rp {e.price.toLocaleString("id-ID")}
                    </td>
                    <td className="py-4 px-6 text-right font-bold text-[var(--brand-azure)]">
                      Rp {e.commission.toLocaleString("id-ID")}
                    </td>
                  </tr>
                ))}
                <tr className="bg-[var(--brand-ink)] text-white">
                  <td className="py-4 px-6 font-semibold">💡 Refer 5 Klien Bulan Ini</td>
                  <td className="py-4 px-6 text-right text-white/60 text-sm">Rata-rata paket Bisnis</td>
                  <td className="py-4 px-6 text-right font-bold text-[var(--brand-gold)] text-lg">
                    ~ Rp 2.175.000
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="cara-kerja" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-azure-soft)] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-azure)]">
              <Zap className="w-3.5 h-3.5" />
              4 Langkah Mudah
            </div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold text-[var(--brand-ink)]">
              Cara Kerja Program Referral
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <div
                key={i}
                className="relative p-6 rounded-2xl bg-white border border-[var(--brand-border)]"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--brand-azure-soft)] text-[var(--brand-azure)]">
                  <s.icon className="w-5 h-5" />
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold text-[var(--brand-ink)]">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-[var(--brand-gray-500)] leading-relaxed">
                  {s.desc}
                </p>
                {i < steps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-1/2 -right-6 -translate-y-1/2 w-5 h-5 text-[var(--brand-gray-300)]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-3xl px-5">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-[var(--brand-ink)]">
              Pertanyaan yang Sering Ditanya
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl bg-[var(--brand-cream)] border border-[var(--brand-border)] p-5 hover:border-[var(--brand-ink)] transition"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-semibold text-[var(--brand-ink)] pr-4">{f.q}</span>
                  <span className="shrink-0 h-7 w-7 rounded-full bg-[var(--brand-ink)] text-white flex items-center justify-center group-open:rotate-45 transition">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-sm text-[var(--brand-gray-500)] leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[var(--brand-cream)]">
        <div className="mx-auto max-w-4xl px-5">
          <div className="rounded-3xl bg-[var(--brand-ink)] text-white p-8 lg:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 dotted-grid opacity-10" />
            <div className="pointer-events-none absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[var(--brand-gold)] blur-3xl opacity-25" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-gold)] text-[var(--brand-ink)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em]">
                <Trophy className="w-3 h-3" /> Mulai Hari Ini
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold leading-tight">
                Siap Mulai Cuan dari<br /> Rekomendasi Anda?
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                Chat saya di WhatsApp, sebutkan nama Anda, dan mulai bagikan info jasa
                ini ke teman UMKM Anda hari ini juga. Komisi 15% menanti.
              </p>
              <a
                href={`https://wa.me/628211001473?text=${waJoin}`}
                target="_blank"
                rel="noreferrer"
                className="cta-shine mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--brand-gold)] text-[var(--brand-ink)] px-6 py-3.5 text-sm font-bold hover:bg-[#ffcc3b] transition"
              >
                <MessageCircle className="w-4 h-4" />
                Gabung via WhatsApp
                <ArrowRight className="w-4 h-4" />
              </a>
              <div className="mt-6">
                <Link to="/" className="text-xs text-white/50 hover:text-white transition">
                  ← Kembali ke Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWA />
    </div>
  );
}
